import { motion } from "framer-motion";
import { ArrowRight, Download, Github, Linkedin, MapPin, Sparkles } from "lucide-react";
import { profile, tickerWords } from "../data/mock";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  return (
    <section
      id="top"
      className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28"
      data-testid="hero-section"
    >
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* LEFT */}
        <div className="lg:col-span-7">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0}
            className="flex flex-wrap items-center gap-2"
          >
            <span
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass"
              data-testid="hero-status-badge"
            >
              <Sparkles className="h-3.5 w-3.5 text-cyan-300" />
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-cyan-50/90">
                Available for AI/ML Engineering Roles
              </span>
            </span>
            <span
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass"
              data-testid="hero-availability-badge"
            >
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-emerald-100/95">
                Open to Remote & Relocation
              </span>
            </span>
            <span
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass"
              data-testid="hero-experience-badge"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-100/95">
                3+ years experience
              </span>
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="font-display mt-8 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95] text-balance"
            data-testid="hero-name"
          >
            <span className="text-white">Meghashyam</span>
            <br />
            <span className="text-gradient-aurora animate-gradient-pan">
              Adimallam<span className="text-cyan-300">.</span>
            </span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-6 font-serif text-2xl sm:text-3xl italic text-cyan-50"
            data-testid="hero-title"
          >
            {profile.title}
            <span className="not-italic font-mono text-base text-cyan-300/70"> // </span>
            <span className="font-display not-italic text-xl sm:text-2xl text-cyan-200/80 font-medium">
              {profile.subtitle}
            </span>
          </motion.p>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-6 max-w-2xl text-base sm:text-lg text-cyan-100/70 leading-relaxed"
            data-testid="hero-intro"
          >
            {profile.hero.intro}
          </motion.p>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-3 max-w-2xl font-mono text-xs sm:text-sm text-cyan-200/60 leading-relaxed"
          >
            {profile.hero.stack}
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={5}
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-cyan-100/70"
          >
            <span className="inline-flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-cyan-300" />
              {profile.location}
            </span>
            <span className="font-mono text-xs text-cyan-200/60">
              MS Data Science · UAB · GPA 4.0
            </span>
          </motion.div>

          {/* CTAs */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={6}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <a
              href="#projects"
              className="group relative inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium text-white bg-gradient-to-r from-cyan-400 via-teal-400 to-sky-500 hover:from-cyan-300 hover:via-teal-300 hover:to-sky-400 transition-[background] duration-300 glow-violet"
              data-testid="hero-view-projects-btn"
            >
              View Projects
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform duration-200" />
            </a>
            <a
              href={profile.resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium text-white glass hover:bg-white/[0.08] transition-colors duration-200"
              data-testid="hero-resume-btn"
            >
              <Download className="h-4 w-4 text-cyan-300" />
              Download Resume
            </a>
            <a
              href={profile.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="inline-flex items-center justify-center h-11 w-11 rounded-full glass text-cyan-100 hover:text-white hover:bg-white/10 transition-colors duration-200"
              data-testid="hero-linkedin-btn"
            >
              <Linkedin className="h-4 w-4" />
            </a>
            <a
              href={profile.github}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
              className="inline-flex items-center justify-center h-11 w-11 rounded-full glass text-cyan-100 hover:text-white hover:bg-white/10 transition-colors duration-200"
              data-testid="hero-github-btn"
            >
              <Github className="h-4 w-4" />
            </a>
          </motion.div>
        </div>

        {/* RIGHT — Photo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] w-full max-w-md mx-auto">
            {/* Multi-color glow halo */}
            <div className="absolute -inset-10 rounded-full opacity-80 blur-3xl">
              <div className="absolute inset-0 bg-gradient-to-tr from-cyan-400/40 via-teal-400/20 to-rose-400/30 rounded-full" />
            </div>

            {/* Gradient ring frame */}
            <div className="relative h-full w-full p-[1.5px] rounded-3xl bg-gradient-to-br from-cyan-300/70 via-teal-300/50 to-rose-300/60">
              <div className="h-full w-full rounded-[22px] overflow-hidden glass-strong relative">
                <img
                  src={profile.photoUrl}
                  alt={profile.name}
                  className="h-full w-full object-cover"
                  style={{ filter: "saturate(1.05) contrast(1.05)" }}
                  data-testid="hero-profile-photo"
                />
                {/* Gradient tint */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#07061A]/85 via-transparent to-cyan-500/10 pointer-events-none" />
                <div className="absolute inset-0 mix-blend-overlay opacity-30 bg-gradient-to-tr from-rose-400/15 via-transparent to-cyan-400/20 pointer-events-none" />

                {/* Info card */}
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between px-4 py-3 rounded-xl glass-strong">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">
                      Engineer.profile
                    </p>
                    <p className="font-display text-sm text-white font-semibold mt-0.5">
                      Meghashyam A.
                    </p>
                  </div>
                  <div className="text-right font-mono text-[10px] text-cyan-200/70">
                    <p>v3.0 · AI/ML</p>
                    <p className="text-cyan-200/50">since 2022</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating metric chips */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="absolute -left-5 top-10 hidden sm:flex flex-col gap-1 px-3.5 py-2.5 rounded-xl glass-strong"
            >
              <span className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/80">
                inference / s
              </span>
              <span className="font-mono text-xl font-semibold text-gradient-aurora">
                3×
              </span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="absolute -right-5 bottom-24 hidden sm:flex flex-col gap-1 px-3.5 py-2.5 rounded-xl glass-strong"
            >
              <span className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/80">
                p50 latency
              </span>
              <span className="font-mono text-xl font-semibold text-gradient-sunset">
                −74%
              </span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="absolute -bottom-6 left-1/2 -translate-x-1/2 hidden md:flex items-center gap-2 px-4 py-2 rounded-full glass-strong"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse-soft" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-100">
                Hallucination 50 → 0%
              </span>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Marquee ticker */}
      <div className="relative mt-24 md:mt-28 overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#07061A] to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#07061A] to-transparent z-10 pointer-events-none" />
        <div className="border-y border-white/5 backdrop-blur-md bg-white/[0.02]">
          <div className="flex animate-marquee whitespace-nowrap py-3">
            {[...tickerWords, ...tickerWords].map((w, i) => (
              <span
                key={i}
                className="font-mono text-xs uppercase tracking-[0.25em] mx-8 inline-flex items-center gap-8"
              >
                <span className={i % 4 === 0 ? "text-cyan-300" : i % 4 === 1 ? "text-cyan-300" : i % 4 === 2 ? "text-pink-300" : "text-indigo-300"}>
                  {w}
                </span>
                <span className="text-rose-400/40">●</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
